import React, { ReactElement, useEffect, useState } from "react";
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from "../../../messages/en-US";
import moment from "moment-timezone";
import { SortDescriptor, process } from "@progress/kendo-data-query";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { Grid, GridColumn as Column, GridToolbar} from "@progress/kendo-react-grid";
import { CustomTextFilterCell } from "../../Shared/Components/Table/CustomTextFilterCell";
import { Button } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { downloadIcon } from "@progress/kendo-svg-icons";
interface Props {
  events: any;
  timezone: string;
}

interface Page {
  skip: number;
  take: number;
}

const page: Page = { skip: 0, take: 30 };

export default function EndpointsEventTable(props: Props): ReactElement {
  const { timezone } = props;
  const localizationService = useLocalization();
  const initialSort: Array<SortDescriptor> = [
    { field: "timestamp", dir: "desc" },
  ];
  const [sort] = React.useState(initialSort);
  const _export = React.useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

   //Creating dataState to prevent issues with virtual scrolling
   const createAppState = (dataState) => {
    return {
      result: process(props.events, dataState),
      dataState: dataState,
    };
  };

  const [dataState, setDataState] = useState<any>(
    createAppState({
      skip: page.skip,
      take: page.take,
      sort: sort
    })
  );
 
  const dataStateChange = (event) => {
    setDataState(createAppState(event.dataState));
  };

  useEffect(() => {
    setDataState( createAppState({
      skip: page.skip,
      take: page.take,
      sort: sort
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);
  
  //Formating Cells
  const OccurredCell = (props: any) => {
    return (
      <td>
        <div>{moment.tz(props.dataItem.timestamp, timezone).format('DD/MM/YYYY @ HH:mm:ss')}</div>
      </td>
      );
  }

  const UploadedCell = (props: any) => {
    return (
      <td>
        <div>{moment.tz(props.dataItem.synced, timezone).format('DD/MM/YYYY @ HH:mm:ss')}</div>
      </td>
      );
  }

  const VolumeDispensedCell = (props: any) => {
    return (
      <td>
        <div>{(props.dataItem.volumeDispensed).toFixed(2)}</div>
      </td>
      );
  }

  const getExcelExportData = () => {
    return process(props.events, {filter: dataState.dataState.filter, sort: dataState.dataState.sort}).data;
  };
  
  return (
    <div>
      <ExcelExport data={getExcelExportData()} ref={_export} collapsible={true} fileName={localizationService.toLanguageString('custom.endpointsEvents', enMessages.custom.endpointsEvents)}>
        <ExcelExportColumn field="timestamp" title="Occurred" cellOptions={{ format: "dd/mm/yyyy hh:mm:ss" }} width={150}/>
        <ExcelExportColumn field="synced" title="Uploaded" cellOptions={{ format: "dd/mm/yyyy hh:mm:ss" }} width={150}/>
        <ExcelExportColumn field="serialNo" title="Device" /> 
        <ExcelExportColumn field="areaName" title="Location" />
        <ExcelExportColumn field="machineName" title="Asset" />
        <ExcelExportColumn field="endpointName" title="Name" />
        <ExcelExportColumn field="consumableName" title="Consumable" />
        <ExcelExportColumn field="volumeDispensed" title="Volume Dispensed" cellOptions={{ format: "0.0000" }}/>
      </ExcelExport>
     
      <Grid
        key={props.events.length.toString()}
        rowHeight={46}
        take={page.take}
        data={dataState.result}
        scrollable={"virtual"}
        filterable={true}
        onDataStateChange={dataStateChange}
        {...dataState.dataState}
        sortable={true}
        style={{
          height: `600px`,
        }}
        className=" w-full z-0 extra-filter-button "
      >
        <GridToolbar >
          <div style={{width:'100%', display:'flex', justifyContent:'space-between'}}>
            <div className="gb-h3 pl-1">{localizationService.toLanguageString('custom.events', enMessages.custom.events)}</div>
              <Tooltip anchorElement="target" position="bottom">
                <Button
                  title={localizationService.toLanguageString('custom.exportToExcel', enMessages.custom.exportToExcel)}
                  svgIcon={downloadIcon}
                  disabled={dataState.result.data.length === 0}
                  onClick={excelExport}
                />
             </Tooltip>
          </div>
        </GridToolbar>
        <Column field="timestamp" title={localizationService.toLanguageString('custom.occurred', enMessages.custom.occurred)} cell={OccurredCell} filterable={false}/>
        <Column field="synced" title={localizationService.toLanguageString('custom.uploaded', enMessages.custom.uploaded)}  cell={UploadedCell} filterable={false}/>
        <Column field="serialNo" title={localizationService.toLanguageString('custom.device', enMessages.custom.device)} filterCell={CustomTextFilterCell}/> 
        <Column field="areaName" title={localizationService.toLanguageString('custom.location', enMessages.custom.location)} filterCell={CustomTextFilterCell} />
        <Column field="machineName" title={localizationService.toLanguageString('custom.asset', enMessages.custom.asset)} filterCell={CustomTextFilterCell}  />
        <Column field="endpointName" title={localizationService.toLanguageString('custom.name', enMessages.custom.name)} filterCell={CustomTextFilterCell}  />
        <Column field="consumableName" title={localizationService.toLanguageString('custom.consumable', enMessages.custom.consumable)} filterCell={CustomTextFilterCell}  />
        <Column field="volumeDispensed" title={localizationService.toLanguageString('custom.volumeDispensed', enMessages.custom.volumeDispensed)} cell={VolumeDispensedCell} filterCell={CustomTextFilterCell} />
      </Grid>     
  </div>
  );
}